import { ArchitectureParseError } from "../errors";
import { splitH3Entries } from "../parse-sections";
import type { Actor, ExternalSystem, ExternalSystemKind, System } from "../types";
import { parseAssumptions } from "./parse-assumptions-v3";
import { asString, parseBoldEntriesV3 } from "./parse-bullets-v3";
import { parseRisks } from "./parse-risks-v3";

// ## L1 — System Context
// ### System          (singular — exactly one entry)
// ### Actors          (one or more fact-type entries)
// ### External Systems (one or more choice-type entries with risks/assumptions)

interface L1Result {
  system?: System;
  actors: Actor[];
  externalSystems: ExternalSystem[];
}

export const EXTERNAL_KIND_VALUES: ReadonlySet<ExternalSystemKind> = new Set([
  "saas",
  "infrastructure",
  "integration",
  "data-source",
  "ai-service",
  "auth-provider",
  "payment-provider",
  "other",
]);

export function parseL1(body: string): L1Result {
  const result: L1Result = { actors: [], externalSystems: [] };
  const subs = splitH3Entries(body);
  for (const sub of subs) {
    const title = sub.title.toLowerCase().trim();
    if (title === "system") {
      result.system = parseSystem(sub.body);
    } else if (title === "actors") {
      result.actors = parseActors(sub.body);
    } else if (title === "external systems") {
      result.externalSystems = parseExternals(sub.body);
    }
    // Other H3 titles are ignored silently — the L1 section is allowed to
    // grow new subsections later without breaking older parsers.
  }
  return result;
}

// Exported so v0.4 (and any future minor revision that only touches the
// externals block) can reuse the unchanged System/Actors parsing without
// duplicating it.
export function parseSystem(body: string): System | undefined {
  const entries = parseBoldEntriesV3(body, "L1.System");
  if (entries.length === 0) return undefined;
  if (entries.length > 1) {
    throw new ArchitectureParseError(
      "L1.System: expected exactly one System entry, found " +
        `${entries.length}. The System block represents the document's subject — pick one.`,
    );
  }
  const entry = entries[0];
  if (!entry) return undefined;
  const p = entry.properties;
  return {
    id: entry.id,
    label: asString(p.label) ?? entry.id,
    headline: asString(p.headline),
    description: asString(p.description),
    why: asString(p.why),
  };
}

export function parseActors(body: string): Actor[] {
  const entries = parseBoldEntriesV3(body, "L1.Actors");
  return entries.map((entry) => {
    const p = entry.properties;
    const why_it_matters = asString(p.why_it_matters);
    const why = asString(p.why);
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      headline: asString(p.headline),
      description: asString(p.description),
      why_it_matters: why_it_matters ?? why,
      // Mirror `why_it_matters` into `why` so the existing renderer keeps
      // working until Phase 5.
      why: why ?? why_it_matters,
      // `type` was a v0.2 actor field; v0.3 drops it but the type interface
      // keeps it required. Default to "person" — actors in v0.3 are people
      // by definition (system-like actors should be modelled as containers
      // or external systems).
      type: "person",
    };
  });
}

export function parseCluster(
  value: unknown,
  scope: string,
): { id: string; label: string } | undefined {
  // Optional L1 sub-boundary grouping. Externals sharing the same cluster.id
  // are wrapped in a single dashed clusterFrame at L1 (e.g. AWS or
  // MICROSOFT 365 STACK). Forgiving on the omitted case (no cluster block
  // at all), strict on the malformed case (block present but incomplete) —
  // a silently-dropped cluster strands externals in the wrong frame, which
  // is harder to debug than a parse error (gap #6/11).
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "object" || Array.isArray(value)) {
    throw new ArchitectureParseError(
      `${scope}.cluster: expected an object with \`id\` and \`label\`.`,
    );
  }
  const rec = value as Record<string, unknown>;
  const id = asString(rec.id) ?? "";
  const label = asString(rec.label) ?? "";
  if (id === "" || label === "") {
    throw new ArchitectureParseError(
      `${scope}.cluster: both \`id\` and \`label\` are required (non-empty).`,
    );
  }
  return { id, label };
}

function parseExternals(body: string): ExternalSystem[] {
  const entries = parseBoldEntriesV3(body, "L1.ExternalSystems");
  return entries.map((entry) => {
    const p = entry.properties;
    const scope = `L1.ExternalSystems.${entry.id}`;
    const rawKind = (asString(p.kind) ?? "").toLowerCase();
    const kind: ExternalSystemKind | undefined =
      rawKind === ""
        ? undefined
        : EXTERNAL_KIND_VALUES.has(rawKind as ExternalSystemKind)
          ? (rawKind as ExternalSystemKind)
          : "other";
    const risks = parseRisks(p.risks, { scope, strict: true });
    const assumptions = parseAssumptions(p.assumptions, { scope, strict: true });
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      headline: asString(p.headline),
      description: asString(p.description),
      why: asString(p.why),
      kind,
      url: asString(p.url),
      cluster: parseCluster(p.cluster, scope),
      risks,
      assumptions,
      // Mirror canonical risks into the deprecated v0.2 `risk` string array
      // so the existing renderer keeps working until Phase 5 migrates it.
      risk: risks.map((r) => r.description),
    };
  });
}
